// @ts-nocheck — mixin methods are composed onto BackendService at runtime; instance API is typed via adminBackendServiceInterface.ts.
import type BaseBackendService from '../BaseBackendService';
import Investor from '../../models/Investor';

export const adminBackendServiceContactPortfolioMixin = {
  getContactInvestments(
    contactId: string,
    options: { includeComplete?: boolean } = {},
  ): Promise<Investor[]> {
    const params = new URLSearchParams();
    if (options.includeComplete) params.set('includeComplete', 'true');
    const qs = params.toString();
    return this.authApiGetAuthed(
      `contacts/${contactId}/investments${qs ? `?${qs}` : ''}`,
      'Network error while fetching contact investments',
      (json) => json?.data ?? [],
    );
  },

  getContactInvestmentOverviewMetrics(
    contactId: string,
    options: { includeClosedFunds?: boolean } = {},
  ): Promise<{
    projectedRoePercent: number | null;
    projectedIrrPercent: number | null;
    moneyMultiple: number | null;
    totalInvested: number | null;
  }> {
    const params = new URLSearchParams();
    if (options.includeClosedFunds) params.set('includeClosedFunds', 'true');
    return this.authApiGetAuthed(
      `contacts/${contactId}/investment-overview-metrics?${params}`,
      'Network error while fetching contact investment overview metrics',
      (json) => json?.data ?? {},
    );
  },

  getContactUpcomingEvents(
    contactId: string,
    windowDays: 30 | 90,
  ): Promise<{
    items: Array<{
      eventExternalId: string;
      eventDate: string;
      eventType: string;
      fundName: string;
      displayAmount: number;
    }>;
  }> {
    return this.authApiGetAuthed(
      `contacts/${contactId}/upcoming-events?windowDays=${windowDays}`,
      'Network error while fetching contact upcoming events',
      (json) => json ?? { items: [] },
    );
  },

  getContactPortfolioSummary(contactId: string): Promise<{
    portfolioSummary: {
      committedInvestment: number;
      deployedCapital: number;
      totalForecastReturn: number;
      activeFunds: number;
      completeFunds: number;
    };
    calculationDetails: {
      fundBreakdown: Array<{
        fundExternalId: string;
        fundName: string;
        investorInvestment: number;
        deployedAmount: number;
        forecastReturn: number;
        irr: number | null;
        roe: number | null;
        mm: number | null;
        latestMonth: string | null;
        scaledTimeSeries: Array<{
          month: string;
          label: string;
          type: 'actual' | 'forecast';
          cumulativeValue: number;
          discreteValue: number;
        }>;
      }>;
    };
  }> {
    return this.authApiGetAuthed(
      `contacts/${contactId}/portfolio-summary`,
      'Network error while fetching contact portfolio summary',
      (json) => json ?? {},
    );
  },

  getContactPortfolioTags(contactId: string): Promise<{
    portfolioSummary: { totalInvestment: number; totalFunds: number };
    tags: {
      descriptive: Record<
        string,
        {
          tagId: string;
          label: string;
          type: string;
          values: Array<{
            value: string;
            label: string;
            fundCount: number;
            investorAmount: number;
          }>;
          displayValue: string;
        }
      >;
      calculated: Record<
        string,
        { tagId: string; label: string; type: string; value: number | null; unit: string }
      >;
    };
  }> {
    return this.authApiGetAuthed(
      `contacts/${contactId}/portfolio-tags`,
      'Network error while fetching contact portfolio tags',
      (json) => json ?? {},
    );
  },
} satisfies ThisType<BaseBackendService>;
